import { Clock, Pin, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { ImagePost } from "../../types";

interface ChatImageGridProps {
    images: ImagePost[];
    onImagePress: (index: number) => void;
    onOpenAll: () => void;
    isAlone?: boolean;
    showMetadata?: boolean;
    timeText?: string;
    isPinned?: boolean;
    isFavorited?: boolean;
    idNotSend?: boolean;
}

export default function ChatImageGrid({
    images,
    onImagePress,
    onOpenAll,
    isAlone = false,
    showMetadata = isAlone,
    timeText,
    isPinned = false,
    isFavorited = false,
    idNotSend = false,
}: ChatImageGridProps) {
    const [ratios, setRatios] = useState<Record<number, number>>({});
    const count = images.length;
    const isPortraitPair = count === 2 && images.every((_, index) => ratios[index] !== undefined && ratios[index] < 0.9);
    const isOverflow = count > 4;
    const visibleImages = isOverflow ? images.slice(0, 4) : images;
    const layout = count === 1
        ? "chat-img-grid--single"
        : count === 2
            ? (isPortraitPair ? "chat-img-grid--two-portrait" : "chat-img-grid--two-stacked")
            : count === 3
                ? "chat-img-grid--three"
                : "chat-img-grid--squares";

    useEffect(() => {
        setRatios({});
    }, [images.map(image => image.path).join("|")]);

    if (!images.length) return null;

    const handleLoad = (index: number, event: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (!naturalWidth || !naturalHeight) return;
        setRatios(previous => ({ ...previous, [index]: naturalWidth / naturalHeight }));
    };

    const metadata = showMetadata && timeText ? (
        <span className="chat-img-timestamp">
            {isPinned && <Pin size={11} style={{ marginRight: 3 }} />}
            {isFavorited && <Star size={11} style={{ marginRight: 3, fill: "currentColor" }} />}
            {timeText}
            {idNotSend && <Clock size={11} style={{ marginLeft: 3, opacity: 0.8 }} />}
        </span>
    ) : null;

    return (
        <div className={`chat-img-grid ${layout}${isAlone ? " chat-img-grid--alone" : ""}`}>
            {visibleImages.map((image, index) => {
                const isOverflowTile = isOverflow && index === visibleImages.length - 1;
                return (
                    <button
                        key={`${image.path}-${index}`}
                        type="button"
                        className="chat-img-cell"
                        onClick={() => (isOverflowTile ? onOpenAll() : onImagePress(index))}
                    >
                        <img
                            src={image.path}
                            alt={`Imagem ${index + 1}`}
                            className="chat-img"
                            draggable={false}
                            onLoad={event => handleLoad(index, event)}
                            onError={event => {
                                event.currentTarget.src = "/img/image_not_found.png?v=1";
                            }}
                        />
                        {isOverflowTile && (
                            <span className="chat-img-overflow">+ {count - 4}</span>
                        )}
                        {index === visibleImages.length - 1 && metadata}
                    </button>
                );
            })}
        </div>
    );
}
